import styles from './Tab.module.scss';
import AvatarWithMarketplace from '@components/AvatarWithMarketplace';
import Box from '@components/Box';
import CardParcelModalWindow from '../modals/CardParcelModalWindow/CardParcelModalWindow';
import cn from 'classnames';
import DataTable, { DataViewHandle } from '@containers/DataTable';
import { Column, Row } from '@components/Grid';
import { FilterModel } from '@hooks/useFiltersSearchParams';
import { Filters, FilterTypes } from '@containers/Filters';
import { formatDate } from '@utils/formatValues';
import { getCurrentLanguage } from '@utils/lang';
import { getResale, TResaleParcel } from '@api/apiClient/returns';
import { IResaleTabProps } from '../types';
import { LinkButton, LinkButtonSize } from '@components/new/LinkButton';
import { Modal } from '@components/Modal';
import ParcelModalWindow from '@containers/ParcelModalWindow';
import { sortByStores } from '@utils/sorting';
import { TableColumn, TableColumnType, TableRowType } from '@components/Table/TableTypes';
import { Text, TextColor, TextSize } from '@components/Text';
import TrackingNumber from "@components/TrackingNumber";
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

const ResaleTab: React.FC<IResaleTabProps> = (props: IResaleTabProps): React.ReactElement => {
  const {
    isFilterOpen,
    setIsFilterOpen
  } = props;

  const [columnsSort, setColumnsSort] = useState<string[]>([]);
  const [innerFilters, setInnerFilters] = useState<FilterModel[]>([]);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const [isOpenNewOrderParcelWindow, setIsOpenNewOrderParcelWindow] = useState(false);
  const [isOpenParcelWindow, setIsOpenParcelWindow] = useState(false);
  const [newOrderId, setNewOrderId] = useState<string>('');
  const [parcel, setParcel] = useState<TResaleParcel | undefined>(undefined);

  const dataRef = useRef<DataViewHandle>(null);

  const currentLanguage = getCurrentLanguage();
  const { t } = useTranslation();

  const handlerSortByStores = (position: number, value: string, filters: FilterModel[]): void => {
    sortByStores({ columnsSort, filters, position, setColumnsSort, setInnerFilters, value });
  };

  const columns: TableColumn<TResaleParcel>[] = useMemo(() => [
    {
      header: t("common.trackingNumber"),
      accessor: (row: TableRowType<TResaleParcel>) => (
        <TrackingNumber
          externalId={row.data?.trackingNumber}
          trackNumber={row.data?.directStreamExternalId}
          onClick={() => {
            setIsOpenParcelWindow(true);
            setParcel(row.data);
          }}
          allowEqualNumbers
        />
      ),
      fixWidth: true,
      noOverflow: true,
      width: '200px'
    },
    {
      header: t('returnsPage.resaleTab.newOrderNumber'),
      accessor: (row: TableRowType<TResaleParcel>) => (
        row.data.newOrderId && row.data.newOrderNumber ? (
          <LinkButton
              onClick={() => {
                setNewOrderId(row.data.newOrderId);
                setIsOpenNewOrderParcelWindow(true);
              }}
              size={LinkButtonSize.S}
              title={row.data.newOrderNumber}
          />
        ) : row.data.newOrderNumber
      ),
      width: '175px'
    },
    {
      header: t('returnsPage.resaleTab.name'),
      accessor: (row: TableRowType<TResaleParcel>) => (
          <a
              className={styles.link}
              href={row?.data?.url}
              rel="noreferrer"
              target="_blank"
          >
            {row?.data?.title}
          </a>
      ),
      width: '235px'
    },
    {
      header: t('returnsPage.resaleTab.store'),
      accessor: (row: TableRowType<TResaleParcel>) => (
          <AvatarWithMarketplace
              id={row.data.seller.id}
              name={row.data.seller.name}
          />
      ),
      alignment: 'center',
      noOverflow: true,
      sortable: true,
      sortableFn: handlerSortByStores
    },
    {
      header: t('returnsPage.resaleTab.date'),
      accessor: (row: TableRowType<TResaleParcel>) => formatDate(row.data.resaleVotedDate, 'dd.MM.yy'),
      type: TableColumnType.TERTIARY
    }
  ], [currentLanguage, columnsSort]);

  useEffect(() => {
    const columnsSort: string[] = columns.map((item: TableColumn<TResaleParcel>) => item.sortable ? 'asc' : '');
    setColumnsSort(columnsSort);
  }, []);

  return (
      <div>
        <Box pb={5}>
          <Text
              color={TextColor.PRIMARY}
              size={TextSize.S}
          >
            {t('returnsPage.resaleTab.info_1')}
          </Text>
        </Box>
        <Box
            pb={5}
            className={cn(!isFilterOpen && styles.hidden)}
        >
          <Column phone={12}>
            <Row
                justify="right"
                gap={5}
            >
              <Filters
                  id="returns-resale"
                  openFilters={() => setIsFilterOpen(true)}
                  items={[
                    {
                      name: 'trackNumbers',
                      type: FilterTypes.MULTI_SEARCH,
                      placeholder: t('filters.searchByTrackNumber')
                    },
                    {
                      name: 'sellerIds',
                      type: FilterTypes.SELLER
                    },
                    {
                      name: 'dateRange',
                      type: FilterTypes.DATE_RANGE,
                      placeholder: t('returnsPage.resaleTab.date'),
                      fieldNames: ['ResaleVoteStartDate', 'ResaleVoteEndDate']
                    }
                  ]}
                  onChange={(v, isReady) => {
                    if (isReady) {
                      setIsInitialized(true);
                    }
                    setInnerFilters(v);
                  }}
                  values={innerFilters}
                  asPanel
              />
            </Row>
          </Column>
        </Box>
        <DataTable<TResaleParcel>
            columns={columns}
            columnsSort={columnsSort}
            dataRef={dataRef}
            defaultPageSize={10}
            filters={innerFilters}
            getData={getResale}
            notFound={t('returnsPage.resaleTab.noParcels')}
            pending={!isInitialized}
            queryKey="resaleParcels"
        />
        <Modal
            bodyWithoutIndentations
            isOpen={isOpenParcelWindow}
            onClickOverlay={() => setIsOpenParcelWindow(false)}
            onClose={() => setIsOpenParcelWindow(false)}
            width={900}
        >
          <CardParcelModalWindow
              callFrom="resaleTab"
              handlerSetIsOpenParcelWindow={setIsOpenParcelWindow}
              parcel={parcel!}
          />
        </Modal>
        <Modal
            bodyWithoutIndentations
            isOpen={isOpenNewOrderParcelWindow}
            onClickOverlay={() => setIsOpenNewOrderParcelWindow(false)}
            onClose={() => setIsOpenNewOrderParcelWindow(false)}
            width={900}
        >
          <ParcelModalWindow parcelId={newOrderId}/>
        </Modal>
      </div>
  );
};

export default ResaleTab;
